/**
 * API Error Normalizer
 *
 * Normalises error responses from the backend into a consistent shape:
 *   { code, message, detail, requestId, status }
 *
 * The backend always returns:
 *   { "error": { "code": "...", "message": "...", "detail": ..., "request_id": "..." } }
 *
 * But legacy endpoints or network failures may return different shapes.
 * This module provides a single normalisation point for all callers.
 */

/**
 * @typedef {Object} NormalizedError
 * @property {string}  code      - Machine-readable error code (e.g. "VALIDATION_ERROR")
 * @property {string}  message   - Human-readable message
 * @property {*}       detail    - Optional structured detail (may be null)
 * @property {string}  requestId - X-Request-ID echoed by the server (may be empty)
 * @property {number}  status    - HTTP status code (0 if network error)
 */

/**
 * Normalise any Axios error or raw error object.
 *
 * @param {unknown} err - The error thrown by the API client
 * @returns {NormalizedError}
 */
export function normalizeApiError(err) {
  // Network / timeout error (no response)
  if (!err || typeof err !== 'object') {
    return {
      code: 'UNKNOWN_ERROR',
      message: String(err) || 'An unknown error occurred',
      detail: null,
      requestId: '',
      status: 0,
    };
  }

  const status = err.status || err.response?.status || 0;
  const responseData = err.data || err.response?.data || {};

  // New backend error shape: { error: { code, message, detail, request_id } }
  if (responseData?.error?.code) {
    const { code, message, detail, request_id } = responseData.error;
    return {
      code,
      message: message || 'An error occurred',
      detail: detail ?? null,
      requestId: request_id || '',
      status,
    };
  }

  // Legacy FastAPI shape: { detail: "..." }
  if (responseData?.detail) {
    const detail = responseData.detail;
    const message =
      typeof detail === 'string' ? detail : 'An error occurred';
    return {
      code: `HTTP_${status}`,
      message,
      detail: typeof detail === 'object' ? detail : null,
      requestId: '',
      status,
    };
  }

  // Network or parse error
  if (err.message) {
    return {
      code: 'NETWORK_ERROR',
      message: err.message,
      detail: null,
      requestId: '',
      status,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    detail: null,
    requestId: '',
    status,
  };
}

/**
 * Map a NormalizedError to a user-facing actionable message.
 *
 * @param {NormalizedError} err
 * @returns {string}
 */
export function toUserMessage(err) {
  switch (err.code) {
    case 'VALIDATION_ERROR':
      return 'Please check your input and try again.';
    case 'RATE_LIMIT_EXCEEDED':
      return 'You are sending requests too quickly. Please wait a moment.';
    case 'SUBSCRIPTION_REQUIRED':
      return 'An active subscription is required. Please upgrade your plan.';
    case 'FEATURE_NOT_AVAILABLE':
      return `This feature is not available on your current plan.`;
    case 'NETWORK_ERROR':
      return 'Could not connect to the server. Check your internet connection.';
    case 'HTTP_401':
      return 'Your session has expired. Please log in again.';
    case 'HTTP_403':
      return 'You do not have permission to perform this action.';
    case 'HTTP_404':
      return 'The requested resource was not found.';
    case 'HTTP_500':
    case 'INTERNAL_SERVER_ERROR':
      return 'A server error occurred. Please try again in a moment.';
    default:
      return err.message || 'An error occurred. Please try again.';
  }
}

/**
 * Returns true if the error indicates the user's Google Ads token is expired/revoked.
 *
 * @param {NormalizedError} err
 * @returns {boolean}
 */
export function isGoogleTokenError(err) {
  return (
    err.code === 'TOKEN_REVOKED' ||
    err.code === 'TOKEN_MISSING' ||
    err.code === 'TOKEN_EXPIRED'
  );
}
